import React, { useState, useRef, useEffect } from "react";
import { Text, Spin, Switch, Tooltip } from "react_reusable_components";

import { TwoColumn, TwoRow } from "../../../../core/layouts";
import Graph from "../../DashboardGraphs/Graphs/LevelGraphs/Graph";
import variables from "../../../../themes/base/styles/variables";
import TableActions from "../../../../components/TableActions";
import { useIntl } from "react-intl";
import useResponsive from "../../../../core/hooks/useResponsive";
import ThreeColumn from "../../../../core/layouts/ThreeColumn";
import {
  getConditionalContent,
  getLocalizedMessage,
} from "../../../../UtilityFunctions/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GRAPH_TOOLTIP } from "../../../../constants";
import GraphTooltipContainer from "../../../GraphTooltipContainer";

export const GraphStructureStyles = {
  box: {
    border: variables.border,
    padding: variables.paddingSmall,
    backgroundColor: variables.bodyBg,
    minHeight: variables.graphsMinHeight,
  },
  headerSection: {
    justifyContent: variables.spaceBetween,
  },
  headerLeftSection: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  bottomSection: {
    paddingTop: variables.paddingSmall,
    // height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  spinner: {
    height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  switch: {
    gap: variables.gapXS1,
    alignItems: variables.alignCenter,
  },
  mobileSwitch: {
    fontSize: variables.fontSizeSmall,
    iconWidth: variables.iconWidth,
  },
  notFoundText: {
    fontSize: variables.fontSizeXlarge,
    textAlign: variables.alignCenter,
    width: variables.fullWidth,
  },
  functionalitiesStyle: {
    gap: variables.gapXS1,
    alignItems: variables.alignCenter,
  },
  tooltipWidth: { width: variables.tooltipWidth },
};

const LevelGraphStructure = ({
  data,
  legend1,
  legend2,
  labelText,
  background,
  fill,
  color2,
  color1,
  linkLabel,
  dataKey1,
  dataKey2,
  dataKey3,
  dataNotFound,
  setDataNotFound,
  isLoading,
  checked,
  setChecked,
  dataKey4,
  downloadUserReportsHandler,
  isPercentageChecked,
  level,
  graphId,
  ...props
}) => {//NOSONAR
  const [graphMounted, setGraphMounted] = useState(false);
  const responsive = useResponsive();
  const intl = useIntl();
  const screenWidth = window.innerWidth;

  // Use a useRef to access the Graph component
  const graphRef = useRef();

  useEffect(() => {
    setGraphMounted(true);
  }, []);

  const downloadChartImage = () => {
    // Call the downloadChartImage function of the Graph component only if it is mounted
    try {
      if (graphMounted && graphRef.current) {
        graphRef.current.downloadChartImage();
      }
    } catch (error) {
      setDataNotFound(error);
    }
  };
  return (
    <TwoRow
      style={GraphStructureStyles.box}
      topSection={
        <TwoColumn
          style={GraphStructureStyles.headerSection}
          leftSection={
            <Text
              type="secondary"
              style={GraphStructureStyles.headerLeftSection}
            >
              {labelText}{" "}
              <Tooltip
                overlayInnerStyle={GraphStructureStyles.tooltipWidth}
                title={
                  <GraphTooltipContainer data={GRAPH_TOOLTIP(intl)[level]} />
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Text>
          }
          rightSection={
            dataNotFound || !data?.length ? null : (
              <ThreeColumn
                style={GraphStructureStyles.switch}
                leftSection={
                  responsive.isMd ? (
                    <Text type="secondary" style={GraphStructureStyles.switch}>
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  ) : (
                    <Text
                      type="secondary"
                      style={GraphStructureStyles.mobileSwitch}
                    >
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  )
                }
                middleSection={
                  <Switch
                    size={getConditionalContent({
                      condition: responsive.isMd,
                      trueValue: "default",
                      falseValue: "small",
                    })}
                    onChange={() => setChecked(!checked)}
                    checked={checked}
                  />
                }
                rightSection={
                  <TwoColumn
                    style={GraphStructureStyles.functionalitiesStyle}
                    leftSection={getConditionalContent({
                      condition: responsive.isMd,
                      trueValue: (
                        <Text
                          type="secondary"
                          style={GraphStructureStyles.switch}
                        >
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      ),
                      falseValue: (
                        <Text
                          type="secondary"
                          style={GraphStructureStyles.mobileSwitch}
                        >
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      ),
                    })}
                    rightSection={
                      <TableActions
                        tableActionsOptions={[
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadGraph.label"
                            ),
                            action: downloadChartImage,
                          },
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadReport.label"
                            ),
                            action: downloadUserReportsHandler,
                          },
                        ]}
                      />
                    }
                  />
                }
              />
            )
          }
        />
      }
      bottomSectionStyle={getConditionalContent({
        condition: isLoading,
        trueValue: {
          ...GraphStructureStyles.spinner,
          ...GraphStructureStyles.bottomSection,
        },
        falseValue: GraphStructureStyles.bottomSection,
      })}
      bottomSection={
        isLoading ? (
          <Spin size={"large"} type={"default"} />
        ) : !dataNotFound && data?.length ? (
          <Graph
            ref={graphRef}
            id="graph"
            data={data}
            width="100%"
            height={getConditionalContent({
              condition: screenWidth < 768,
              trueValue: 310,
              falseValue: 400,
            })}
            barSize={19}
            dataKey1={dataKey1}
            dataKey2={dataKey2}
            dataKey3={dataKey3}
            dataKey4={dataKey4}
            name={getLocalizedMessage(intl, "levelGraphs.legend1.label")}
            axisLine={false}
            color1={color1}
            color2={color2}
            name2={getLocalizedMessage(intl, "levelGraphs.legend2.label")}
            background={background}
            fill={fill}
            linkLabel={linkLabel}
            legend1={legend1}
            legend2={legend2}
            setDataNotFound={setDataNotFound}
            isPercentageChecked={isPercentageChecked}
            graphId={graphId}
          />
        ) : (
          <Text
            style={{
              ...GraphStructureStyles.notFoundText,
              marginTop: getConditionalContent({
                condition: responsive.isSm,
                trueValue: "20%",
                falseValue: "40%",
              }),
            }}
          >
            {dataNotFound ||
              getLocalizedMessage(intl, "level.doesNotExist", {
                level: level.charAt(5),
              })}
          </Text>
        )
      }
    />
  );
};

export default LevelGraphStructure;
